import { useEffect, useState } from 'react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { fetchLicenseInformation, type LicenseInformation } from './licenseInformation.ts';

export type MeetingsLicenseInfo = {
	/** Still fetching (or not yet started while enabled). */
	loading: boolean;
	/** The license-information payload, or `null` when skipped/failed. */
	info: LicenseInformation;
};

/**
 * Fetch the site's xflow license information once per mount, used to derive the
 * premium-tier targeting signal (Loom Business + AI / TWC Premium).
 *
 * Gated by `enabled` so the extra REST request never fires on the feed critical
 * path for gate-off users — mirroring the `skip: !enabled` discipline of the
 * meetings Relay query. A failed fetch resolves to `info: null` (treated as
 * "no premium tier" downstream), so a license-info outage safely excludes a
 * site from co-use rather than mis-surfacing it.
 */
export const useMeetingsLicenseInfo = (enabled: boolean): MeetingsLicenseInfo => {
	const { cloudId } = useSessionData();
	const [state, setState] = useState<MeetingsLicenseInfo>({ loading: enabled, info: null });

	useEffect(() => {
		if (!enabled || !cloudId) {
			setState({ loading: false, info: null });
			return;
		}

		let cancelled = false;
		setState({ loading: true, info: null });

		fetchLicenseInformation(cloudId)
			.then((info) => {
				if (!cancelled) {
					setState({ loading: false, info });
				}
			})
			.catch(() => {
				if (!cancelled) {
					setState({ loading: false, info: null });
				}
			});

		return () => {
			cancelled = true;
		};
	}, [enabled, cloudId]);

	return state;
};
